import { api } from './client';
import type { ApiResponse } from '../types';

export interface RestoreRowDiff {
  table: string; identity: string; label?: string; action: 'recreate' | 'restore' | 'draft_newer' | 'remove_association';
  before_sha256: string | null; after_sha256: string | null; changed_fields: string[];
}
export interface RestoreSettingDiff { key: string; before_json: string | null; after_json: string | null }
export interface RestoreFileDiff { scope: 'theme' | 'site_static'; path: string; before_sha256: string | null; after_sha256: string | null; before_size_bytes: number | null; after_size_bytes: number | null }
export interface RestoreSchemaDiff { kind: 'create_table' | 'add_column' | 'create_index'; table: string; summary: string; sql: string }
export interface RestoreWorkConflict { kind: string; operation_id: string; state: string; content_type: string; content_id: string; guidance: string }
export interface RestoreReview {
  operation_id: string; snapshot_id: string; fingerprint: string; can_apply: boolean; requires_operator: boolean;
  rows: RestoreRowDiff[]; settings: RestoreSettingDiff[]; files: RestoreFileDiff[]; schema_changes: RestoreSchemaDiff[];
  warnings: string[]; conflicts: string[]; work: RestoreWorkConflict[]; scheduled_post_ids: string[];
  external_writers_quiescence_required: boolean; build_required: true; publishes: false;
}
export interface RestoreResult {
  operation_id: string; snapshot_id: string; blueprint_id: number; completed_at: string;
  build_required: true; restored_rows: number; newer_rows_drafted: number; warnings: string[];
}
export interface RestoreAbandonReview {
  operation_id: string; snapshot_id: string; fingerprint: string; can_abandon: boolean;
  external_writers_quiescence_required: boolean; blueprint_id: number | null;
  retained_schema_changes: RestoreSchemaDiff[]; warnings: string[]; conflicts: string[];
  content_restored: false; publishes: false;
}
export interface RestoreAbandonResult {
  operation_id: string; snapshot_id: string; abandoned_at: string; abandoned_by_user_id: number;
  content_restored: false; publishes: false; retained_schema_change_count: number;
}
export interface AbandonSnapshotRestore { confirmed_fingerprint: string; external_writers_quiesced: boolean }
export interface RestoreStatus {
  operation_id: string; snapshot_id: string; phase: 'prepared' | 'blueprint_ready' | 'content_committed' | 'internal_committed' | 'complete' | 'abandoning' | 'abandoned';
  outcome: 'restored' | 'abandoned' | null; can_abandon: boolean; abandonment: RestoreAbandonResult | null;
  completed: boolean; can_resume: boolean; confirmed_fingerprint: string; started_by_user_id: number;
  external_writers_quiescence_required: boolean; build_required: boolean; cleanup_pending: boolean;
  result: RestoreResult | null; failure_code?: string; message?: string;
}
export interface ApplySnapshotRestore { operation_id: string; confirmed_fingerprint: string; external_writers_quiesced: boolean }

export interface SnapshotRetention {
  mode: 'hosted' | 'self_hosted'; effective_limit: number; retained_count: number; can_edit: boolean;
  pending_review?: {
    requested_limit: number; prune_count: number; review_fingerprint: string | null;
    phase: 'prepared' | 'pruned' | 'applied' | 'review_required'; can_resume: boolean;
  } | null;
}
export interface SnapshotRetentionPreview {
  current_limit: number; requested_limit: number; prune_count: number; review_fingerprint: string;
}
export interface UpdateSnapshotRetention {
  limit: number; review_fingerprint: string; confirm_prune: true;
}
export interface SnapshotPageParams { limit?: number; offset?: number }
export interface SnapshotTheme {
  kind: string; name: string; base: string | null; source_scope: string;
  source_app_version: string; tree_sha256: string;
}
export interface SnapshotEntry {
  path: string; sha256: string; size_bytes: number; kind: string;
  identity: string | null; slug: string | null;
  encoding: 'binary' | 'utf8' | 'canonical_json_v1';
}
export interface SnapshotDiffTotals { added: number; changed: number; deleted: number; renamed: number }
export interface SnapshotSummary {
  id: string; sequence: number; built_at: string; format_version: number; schema_version: number;
  app_version: string; source_commit: string | null; theme: SnapshotTheme;
  theme_bytes: 'retained' | 'unavailable'; counts: Record<string, number>; entry_count: number;
  previous_id: string | null; baseline: 'available' | 'unavailable';
  changes: SnapshotDiffTotals | null; limitations: string[];
}
export interface SnapshotList {
  versions: SnapshotSummary[]; limit: number; offset: number; has_more: boolean;
}
export interface SnapshotDetail {
  version: SnapshotSummary; entries: SnapshotEntry[]; limit: number; offset: number; has_more: boolean;
}
export interface SnapshotChange {
  change: 'added' | 'changed' | 'deleted' | 'renamed';
  before: SnapshotEntry | null; after: SnapshotEntry | null; binary: boolean;
}
export interface SnapshotComparison {
  from_id: string; to_id: string; totals: SnapshotDiffTotals; changes: SnapshotChange[];
  limit: number; offset: number; has_more: boolean;
  from_theme_bytes: 'retained' | 'unavailable'; to_theme_bytes: 'retained' | 'unavailable';
}
export interface SnapshotEntryView {
  snapshot_id: string; path: string; state: 'text' | 'binary' | 'unavailable';
  entry: Omit<SnapshotEntry, 'path'> | null; text: string | null;
  /** Byte offsets, always on UTF-8 boundaries. Use next_offset verbatim for the next chunk. */
  offset: number; next_offset: number | null; total_bytes: number | null; reason: string | null;
}
function query(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, String(value));
  return search.size ? `?${search}` : '';
}
const base = (siteSlug: string) => `/v1/sites/${encodeURIComponent(siteSlug)}/snapshots`;

/** Saved versions. File access uses an authorized version + logical path, never a hash. */
export const snapshotsApi = {
  abandonPreflight: (siteSlug: string, operationId: string) => api.post<ApiResponse<RestoreAbandonReview>>(`/v1/sites/${encodeURIComponent(siteSlug)}/snapshot-restores/${encodeURIComponent(operationId)}/abandon-preflight`, {}),
  abandonRestore: (siteSlug: string, operationId: string, data: AbandonSnapshotRestore) => api.post<ApiResponse<RestoreAbandonResult>>(`/v1/sites/${encodeURIComponent(siteSlug)}/snapshot-restores/${encodeURIComponent(operationId)}/abandon`, data),
  activeRestore: (siteSlug: string) => api.get<ApiResponse<RestoreStatus | null>>(`/v1/sites/${encodeURIComponent(siteSlug)}/snapshot-restores`),
  restorePreflight: (siteSlug: string, id: string, operation_id: string) => api.post<ApiResponse<RestoreReview>>(`${base(siteSlug)}/${encodeURIComponent(id)}/restore-preflight`, { operation_id }),
  restore: (siteSlug: string, id: string, data: ApplySnapshotRestore) => api.post<ApiResponse<RestoreResult>>(`${base(siteSlug)}/${encodeURIComponent(id)}/restore`, data),
  restoreStatus: (siteSlug: string, operationId: string) => api.get<ApiResponse<RestoreStatus>>(`/v1/sites/${encodeURIComponent(siteSlug)}/snapshot-restores/${encodeURIComponent(operationId)}`),
  retention: (siteSlug: string) => api.get<ApiResponse<SnapshotRetention>>(`${base(siteSlug)}/retention`),
  previewRetention: (siteSlug: string, limit: number) => api.post<ApiResponse<SnapshotRetentionPreview>>(`${base(siteSlug)}/retention/preview`, { limit }),
  updateRetention: (siteSlug: string, data: UpdateSnapshotRetention) => api.put<ApiResponse<SnapshotRetention>>(`${base(siteSlug)}/retention`, data),
  list: (siteSlug: string, params: SnapshotPageParams = {}) =>
    api.get<ApiResponse<SnapshotList>>(`${base(siteSlug)}${query({ ...params })}`),
  get: (siteSlug: string, id: string, params: SnapshotPageParams = {}) =>
    api.get<ApiResponse<SnapshotDetail>>(`${base(siteSlug)}/${encodeURIComponent(id)}${query({ ...params })}`),
  entry: (siteSlug: string, id: string, key: string, params: SnapshotPageParams = {}) =>
    api.get<ApiResponse<SnapshotEntryView>>(`${base(siteSlug)}/${encodeURIComponent(id)}/entry${query({ key, ...params })}`),
  compare: (siteSlug: string, from: string, to: string, params: SnapshotPageParams = {}) =>
    api.get<ApiResponse<SnapshotComparison>>(`${base(siteSlug)}/compare${query({ from, to, ...params })}`),
};
